import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { getReadableErrorMessage } from "../utils/errorHandler";
import { getHomePath, getVerifyPath } from "../utils/localePaths";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function EmailVerificationResult() {
  const { t, locale } = useTranslation();
  const loginPath = locale === 'tr' ? '/tr/giris' : `/${locale}/login`;
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage(t('auth.verify.invalid_link'));
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/verify-email?token=${token}`);
        
        if (response.data.status === 'success' || response.data.status === 'already_verified') {
          setStatus('success');
          setMessage(getReadableErrorMessage({ response: { data: { code: response.data.code } } }, t, 'auth.verify.success_title'));
          
          // Clear pending email from localStorage
          localStorage.removeItem('pending_verification_email');
          
          // If backend returned access_token, auto-login and redirect to home
          if (response.data.access_token) {
            // Auto-login using the token
            await loginWithToken(response.data.access_token, response.data.user);
            
            // Redirect to home after 3 seconds
            setTimeout(() => {
              navigate(getHomePath(locale));
            }, 3000);
          } else {
            // Fallback: redirect to login after 3 seconds
            setTimeout(() => {
              navigate(loginPath);
            }, 3000);
          }
        }
      } catch (error) {
        console.error("Verification error:", error);
        setStatus('error');
        setMessage(getReadableErrorMessage(error, t, 'auth.verify.error'));
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#FFDD59] mx-auto mb-4" />
          <p className="text-gray-400">{t('auth.verify.verifying')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900 border-2 border-[#FFDD59]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'success' ? (
              <CheckCircle2 className="w-16 h-16 text-green-500" />
            ) : (
              <XCircle className="w-16 h-16 text-red-500" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            {status === 'success' ? t('auth.verify.success_title') : t('auth.verify.fail_title')}
          </CardTitle>
          <CardDescription className="text-gray-300 mt-2">
            {status === 'success' ? t('auth.verify.success_desc') : message}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {status === 'success' ? (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-sm text-green-300 text-center">
                  {t('auth.verify.auto_login')}
                </p>
              </div>
              <div className="flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#FFDD59]" />
              </div>
              <p className="text-xs text-gray-400 text-center">
                {t('auth.verify.redirecting')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-300">
                  {t('auth.verify.link_expired')}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate(getVerifyPath(locale))}
                  className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                >
                  {t('auth.verify.request_new')}
                </Button>
                <Button
                  onClick={() => navigate(loginPath)}
                  variant="outline"
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  {t('auth.verify.back_register')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default EmailVerificationResult;
